import React, {useState, useRef, useEffect, useCallback} from 'react';
import {Box, DropButton, Form, Text} from 'grommet';
import { IconCircleX as IconCancel, IconCirclePlus as IconAdd } from '@tabler/icons-react';
import FontAwesomeIcon from "../FontAwesomeIcon";
import { nextIndexInList } from "../../utils/listKeyboard";

const BaseFilter = ({ filterName, renderContent, renderButtonLabel, isActive=false, onReset, onApply}) => {
    const [open, setOpen] = useState(false);
    const dropRef = useRef(null);

    // Only the checkboxes are roved over. A Select, a text input or a calendar in
    // the same popup owns its own arrow keys, so filters built from those keep
    // behaving exactly as they did.
    const getOptions = useCallback(
        () => (dropRef.current
            ? Array.from(dropRef.current.querySelectorAll('input[type="checkbox"]'))
            : []),
        []
    );

    // Listened for on the document rather than on the popup, because Grommet
    // focuses the popup shell as it opens: the shell is an ancestor of this
    // content, so its keydowns never reach a handler nested inside it.
    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (event) => {
            const direction = event.key === 'ArrowDown' ? 'down'
                : event.key === 'ArrowUp' ? 'up'
                    : null;
            if (!direction) return;

            const options = getOptions();
            if (options.length === 0) return; // this filter's content owns its arrows

            const active = document.activeElement;
            const index = options.indexOf(active);
            const inList = index !== -1;
            // Focus is either on one of the options, or on the shell Grommet
            // focuses as the popup opens. Matched by node, not by "is an ancestor
            // of the content": document.body is an ancestor too, so that version
            // swallowed every arrow key on the page whenever focus fell back to
            // the body, and blocked scrolling until the popup closed.
            const shell = dropRef.current ? dropRef.current.closest('[tabindex]') : null;
            const onShell = !inList && !!shell && active === shell;
            if (!inList && !onShell) return;

            const next = nextIndexInList({
                index: inList ? index : null,
                direction,
                count: options.length,
            });
            if (next === null || !options[next]) return;

            event.preventDefault();
            options[next].focus();
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, getOptions]);

    const applyFilter = () => {
        if (open) setOpen(false);
        if (onApply) onApply();
    }

    const resetFilter = (e) => {
        e.stopPropagation();
        if (open) setOpen(false);
        if (onReset) onReset();
    };

    return (
        <DropButton
            // Without this every filter chip announces itself as "Open Drop".
            a11yTitle={filterName}
            hoverIndicator="hover"
            open={open}
            onOpen={() => setOpen(true)}
            onClose={resetFilter}
            dropProps={{
                stretch: false
            }}
            dropContent={
                <Box ref={dropRef} resize={false} border={true} round="xsmall" pad="small" gap="xsmall" responsive={false} width={{min: "small"}}>
                    <Text weight={500}>Filter by {filterName}</Text>
                    <Form onSubmit={applyFilter}>
                        {renderContent()}
                    </Form>
                </Box>
            }
            dropAlign={{ top: 'bottom', left: 'left' }}
        >
            <Box direction="row" gap="xsmall" responsive={false} border pad={{ vertical: "xxsmall", horizontal: "xsmall" }} round="xsmall" background="foreground" wrap={false} flex={false}>
                <Box alignSelf="center" focusIndicator={false} flex={false} {...(isActive ? { onClick: resetFilter } : {})}>
                    <FontAwesomeIcon SelectedIcon={!isActive ? IconAdd : IconCancel} color="text-weak" size="xsmall" />
                </Box>
                <Text size="small" weight={500} style={{ whiteSpace: 'nowrap' }}>{filterName}</Text>
                {renderButtonLabel() && (
                    <Box gap="xsmall" direction="row" wrap={false} flex={false}>
                        <Text color="text-weak" size="small">|</Text><Text size="small" weight="bold" color="magento" style={{ whiteSpace: 'nowrap' }}>{renderButtonLabel()}</Text>
                    </Box>
                )}
            </Box>
        </DropButton>
    );
};

export default BaseFilter;
